export type SetState<T> = (partial: T | Partial<T> | ((state: T) => T | Partial<T>), replace?: boolean) => void;

export type SubscribeOptions<U> = { equalityFn?: (a: U, b: U) => boolean; fireImmediately?: boolean };

export type Subscribe<T> = {
  (listener: (state: T, prevState: T) => void): () => void;
  <U>(selector: (state: T) => U, listener: (selectedState: U, previousSelectedState: U) => void, options?: SubscribeOptions<U>): () => void;
};

export type StoreApi<T> = {
  getState: () => T;
  getInitialState: () => T;
  setState: SetState<T>;
  subscribe: Subscribe<T>;
};

export type StateCreator<T> = (set: SetState<T>, get: () => T, api: StoreApi<T>) => T;

const createImpl = <T>(createState: StateCreator<T>): StoreApi<T> => {
  let state: T;
  const listeners = new Set<(state: T, prevState: T) => void>();

  const setState: SetState<T> = (partial, replace) => {
    const nextState = typeof partial === 'function' ? (partial as (state: T) => T | Partial<T>)(state) : partial;
    if (Object.is(nextState, state)) return;
    const previousState = state;
    state = replace ? (nextState as T) : Object.assign({}, state, nextState);
    for (const listener of listeners) listener(state, previousState);
  };

  const getState = () => state;
  const getInitialState = () => initialState;

  const subscribe = ((listener: (state: T, prevState: T) => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }) as Subscribe<T>;

  const api: StoreApi<T> = { getInitialState, getState, setState, subscribe };
  const initialState = (state = createState(setState, getState, api));
  return api;
};

/**
 * Framework-agnostic store. Mirrors `zustand/vanilla`'s `createStore`, including the
 * curried `createStore<T>()(init)` form used for explicit typing.
 */
export function createStore<T>(): (createState: StateCreator<T>) => StoreApi<T>;
export function createStore<T>(createState: StateCreator<T>): StoreApi<T>;
export function createStore<T>(createState?: StateCreator<T>) {
  return createState ? createImpl(createState) : createImpl;
}
